import { getTrack } from '../tracks';
import { Track } from '../sim/track';
import { FIXED_DT_MS } from '../config';
import type { Scenario, ScenarioCar, ScenarioSpawn } from './types';

/** Sample situé à `dist` px d'arc DEVANT le sample de départ (sens de la course). */
function sampleAhead(track: Track, dist: number): number {
  const s = track.samples;
  const n = s.length;
  let j = 0;
  let acc = 0;
  for (let guard = 0; guard < n && acc < dist; guard++) {
    acc += s[j].segLen;
    j = (j + 1) % n;
  }
  return j;
}

/** Position de grille : `fwd` px d'arc après la ligne, décalée latéralement (`lane` = -1/0/+1). */
function slot(track: Track, fwd: number, lane: number): ScenarioSpawn {
  const sm = track.samples[sampleAhead(track, fwd)];
  const LANE = 22;
  return {
    x: sm.x + sm.nx * LANE * lane,
    y: sm.y + sm.ny * LANE * lane,
    angle: Math.atan2(sm.ty, sm.tx),
  };
}

// Grille : tous JUSTE APRÈS la ligne (progression croissante → classement correct dès le départ).
// Les IA devant, le joueur seul au fond. fwd = recul décroissant vers le joueur.
const AI_GRID: { id: string; ai: string; fwd: number; lane: number }[] = [
  { id: 'ai1', ai: 'fast', fwd: 134, lane: -1 },
  { id: 'ai2', ai: 'normal', fwd: 134, lane: 1 },
  { id: 'ai3', ai: 'normal', fwd: 86, lane: -1 },
  { id: 'ai4', ai: 'slow', fwd: 86, lane: 1 },
];

/**
 * Construit une COURSE : le joueur (cars[0], pour le clavier + la caméra) au fond de la grille,
 * 4 IA devant. Le joueur remonte le peloton. Grille dérivée du circuit.
 */
export function buildRace(track: string, vehicle: string): Scenario {
  const name = getTrack(track) ? track : 'stadium';
  const t = new Track(getTrack(name)!);
  const cars: ScenarioCar[] = [
    { id: 'player', ai: null, vehicle, spawn: slot(t, 38, 0) }, // joueur au fond, centre
    ...AI_GRID.map((g) => ({ id: g.id, ai: g.ai, spawn: slot(t, g.fwd, g.lane) })),
  ];
  return { name: `race-${name}-${vehicle}`, seed: 1, track: name, fixedDtMs: FIXED_DT_MS, cars, inputs: [], captures: [] };
}

// Ordre des profils dans le peloton : un `fast` au FOND (ai1) doit remonter tout le monde
// → dépassements garantis ; alternance de vitesses pour créer des rattrapages en chaîne.
const PACK_PROFILES = ['fast', 'normal', 'slow', 'fast', 'normal', 'slow', 'fast', 'normal'];

/**
 * Peloton dense de `n` IA (sans joueur) : deux par rangée (couloirs opposés), rangées espacées,
 * les plus rapides mêlés aux plus lents → force l'évitement et les dépassements. Sert de banc
 * d'essai M5 (tests Vitest + screenshot) : « course disputée sans IA bloquée ».
 */
export function buildPack(track: string, n = 8): Scenario {
  const name = getTrack(track) ? track : 'stadium';
  const t = new Track(getTrack(name)!);
  const cars: ScenarioCar[] = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / 2);
    const lane = i % 2 === 0 ? -1 : 1;
    cars.push({ id: `ai${i + 1}`, ai: PACK_PROFILES[i % PACK_PROFILES.length], spawn: slot(t, 40 + row * 42, lane) });
  }
  return { name: `pack-${name}`, seed: 7, track: name, fixedDtMs: FIXED_DT_MS, cars, inputs: [], captures: [] };
}
